import UserImageIcon from "@/assets/icons/image-user-plus.svg";
import CenteredSpinner from "@/components/CenteredSpinner";
import { Button } from "@/components/ui/button";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Spinner from "@/components/ui/spinner";
import UserCustomSignatureParams from "@/domain/entities/user/UserCustomSignature";
import getSignatureTemplate from "@/hooks/signature/getTemplate";
import useCreateUserSignature from "@/hooks/signature/useCreateUserSignature";
import useDeleteUserSignature from "@/hooks/signature/useDeleteUserSignature";
import useGetUserSignature from "@/hooks/signature/useGetUserSignature";
import useUpdateUserSignature from "@/hooks/signature/useUpdateUserSignature";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { useToast } from "@/hooks/utils/useToast";
import sanitizeHTML from "@/lib/sanitizeHTML";
import { X } from "lucide-react";
import React, {
    ChangeEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";

// The SDK automatically resizes JPEG/PNG images to 200×200px before saving
// (see resizeImageToBase64.ts in the SDK). On web the file input already restricts
// to image/jpeg and image/png, so unsupported-format bypass is not a concern here.
// Keep this limit in sync with the mobile equivalent:
// apps/mobile/src/presentation/components/content/settings/SigningSettingsContent.tsx
const MAX_AVATAR_FILE_SIZE = 4 * 1024 * 1024;

const SignatureSettings = () => {
    const { data: user } = useGetUserProfile();
    const { t } = useTranslation();
    const fileUploadRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();
    const {
        createUserSignature: {
            mutateAsync: createUserSignature,
            isPending: createUserSignaturePending,
        },
    } = useCreateUserSignature();
    const {
        updateUserSignature: {
            mutateAsync: updateUserSignature,
            isPending: updateUserSignaturePending,
        },
    } = useUpdateUserSignature();
    const {
        deleteUserSignature: {
            mutateAsync: deleteUserSignature,
            isPending: deleteUserSignaturePending,
        },
    } = useDeleteUserSignature();
    const {
        userSignatureParams: {
            data: userSignatureParams,
            isPending: userSignatureParamsPending,
        },
    } = useGetUserSignature();
    const [avatarDataUrl, setAvatarDataUrl] = useState<string | undefined>(
        undefined,
    );
    const [name, setName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [jobTitle, setJobTitle] = useState<string>("");
    const [company, setCompany] = useState<string>("");
    const [website, setWebsite] = useState<string>("");
    const [other, setOther] = useState<string>("");

    const getAvatarDataUrl = (file: File): void => {
        const reader = new FileReader();
        reader.onloadend = () => {
            setAvatarDataUrl(reader.result?.toString());
        };
        reader.readAsDataURL(file);
    };

    const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const file = input.files?.[0];
        if (file) {
            if (file.size > MAX_AVATAR_FILE_SIZE) {
                toast({
                    description: t("settings.signature.form.fileSizeError"),
                    variant: "error",
                });

                input.value = "";

                return;
            }
            getAvatarDataUrl(file);
        }
    };

    const handleImageUpload = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        fileUploadRef.current?.click();
    };

    const areAllFieldsEmpty = (): boolean =>
        !avatarDataUrl &&
        !name.trim() &&
        !email.trim() &&
        !phone.trim() &&
        !jobTitle.trim() &&
        !company.trim() &&
        !website.trim() &&
        !other.trim();

    const handleFormSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ) => {
        event.preventDefault();

        if (!user) {
            return;
        }

        if (userSignatureParams?.id) {
            if (areAllFieldsEmpty()) {
                await deleteUserSignature(userSignatureParams.id);

                setAvatarDataUrl(undefined);
                setName("");
                setEmail("");
                setPhone("");
                setJobTitle("");
                setCompany("");
                setWebsite("");
                setOther("");

                return;
            }

            await updateUserSignature({
                id: userSignatureParams?.id,
                userId: user.id,
                isFavorite: userSignatureParams?.isFavorite,
                avatarURL: avatarDataUrl,
                name,
                email,
                phone,
                jobTitle,
                companyName: company,
                website,
                other,
            });
        } else {
            if (areAllFieldsEmpty()) {
                return;
            }

            await createUserSignature({
                avatar: avatarDataUrl,
                name,
                email,
                phone,
                jobTitle,
                companyName: company,
                website,
                other,
            });
        }
    };

    const handleDeleteSignature = async (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        event.preventDefault();

        if (userSignatureParams?.id) {
            await deleteUserSignature(userSignatureParams.id);

            setAvatarDataUrl(undefined);
            setName("");
            setEmail("");
            setPhone("");
            setJobTitle("");
            setCompany("");
            setWebsite("");
            setOther("");
        }
    };

    const signaturePreview = useMemo(() => {
        const hasContent = !!(
            avatarDataUrl ||
            name.trim() ||
            email.trim() ||
            phone.trim() ||
            jobTitle.trim() ||
            company.trim() ||
            website.trim() ||
            other.trim()
        );

        if (!hasContent) {
            return "";
        }

        const params = new UserCustomSignatureParams({
            id: userSignatureParams?.id || "",
            userId: userSignatureParams?.userId || "",
            isFavorite: userSignatureParams?.isFavorite ?? false,
            avatarURL: avatarDataUrl || undefined,
            name: name || undefined,
            email: email || undefined,
            phone: phone || undefined,
            jobTitle: jobTitle || undefined,
            companyName: company || undefined,
            website: website || undefined,
            other: other || undefined,
        });

        return sanitizeHTML(getSignatureTemplate(params));
    }, [
        avatarDataUrl,
        name,
        email,
        phone,
        jobTitle,
        company,
        website,
        other,
        userSignatureParams?.id,
    ]);

    useEffect(() => {
        if (!userSignatureParams) {
            return;
        }

        setAvatarDataUrl(userSignatureParams.avatarURL || "");
        setName(userSignatureParams.name || "");
        setEmail(userSignatureParams.email || "");
        setPhone(userSignatureParams.phone || "");
        setJobTitle(userSignatureParams.jobTitle || "");
        setCompany(userSignatureParams.companyName || "");
        setWebsite(userSignatureParams.website || "");
        setOther(userSignatureParams.other || "");
    }, [userSignatureParams]);

    if (userSignatureParamsPending || !user) {
        return <CenteredSpinner />;
    }

    return (
        <div className="flex flex-col gap-8">
            <DialogHeader>
                <DialogTitle className="font-bold leading-none">
                    {t("settings.signature.title")}
                </DialogTitle>
                <p className="text-xs">{t("settings.signature.description")}</p>
            </DialogHeader>
            <form className="flex flex-col gap-4" onSubmit={handleFormSubmit}>
                <div className="flex gap-6">
                    <div className="flex w-35 flex-col gap-1">
                        <Label className="mt-1.5" htmlFor="avatar">
                            {t("settings.signature.form.avatar")}
                        </Label>
                        <div className="relative h-full flex-1 flex-col">
                            {avatarDataUrl && (
                                <button
                                    className="absolute bottom-1 right-1 rounded-full border border-gray-300 bg-red-100 p-1"
                                    onClick={() => {
                                        setAvatarDataUrl(undefined);
                                    }}
                                    type="button"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                            <button
                                className="flex h-full w-full flex-1 items-center justify-center rounded-md border border-dashed border-gray-300 bg-surface-primary p-2 text-center text-sm text-gray-500"
                                onClick={handleImageUpload}
                                type="button"
                            >
                                {avatarDataUrl ? (
                                    <img
                                        alt=""
                                        className="h-20 w-20 rounded-full object-cover"
                                        src={avatarDataUrl}
                                    />
                                ) : (
                                    <div className="flex flex-col items-center gap-2">
                                        <img
                                            alt=""
                                            className="h-8 w-8"
                                            src={UserImageIcon}
                                        />
                                        {t(
                                            "settings.signature.form.uploadImage",
                                        )}
                                    </div>
                                )}
                            </button>
                            <input
                                ref={fileUploadRef}
                                accept="image/png, image/jpeg"
                                onChange={onFileChange}
                                type="file"
                                hidden
                            />
                        </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-4">
                        <div className="flex gap-6">
                            <div className="flex flex-1 flex-col gap-4">
                                <div>
                                    <Label htmlFor="name">
                                        {t("settings.signature.form.name")}
                                    </Label>
                                    <Input
                                        id="name"
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                        type="text"
                                        value={name}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="email">
                                        {t("settings.signature.form.email")}
                                    </Label>
                                    <Input
                                        id="email"
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        type="email"
                                        value={email}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="phone">
                                        {t("settings.signature.form.phone")}
                                    </Label>
                                    <Input
                                        id="phone"
                                        onChange={(e) =>
                                            setPhone(e.target.value)
                                        }
                                        type="tel"
                                        value={phone}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-1 flex-col gap-4">
                                <div>
                                    <Label htmlFor="jobTitle">
                                        {t("settings.signature.form.jobTitle")}
                                    </Label>
                                    <Input
                                        id="jobTitle"
                                        onChange={(e) =>
                                            setJobTitle(e.target.value)
                                        }
                                        type="text"
                                        value={jobTitle}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="company">
                                        {t("settings.signature.form.company")}
                                    </Label>
                                    <Input
                                        id="company"
                                        onChange={(e) =>
                                            setCompany(e.target.value)
                                        }
                                        type="text"
                                        value={company}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="website">
                                        {t("settings.signature.form.website")}
                                    </Label>
                                    <Input
                                        id="website"
                                        onChange={(e) =>
                                            setWebsite(e.target.value)
                                        }
                                        type="text"
                                        value={website}
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="other">
                                {t("settings.signature.form.other")}
                            </Label>
                            <Input
                                id="other"
                                onChange={(e) => setOther(e.target.value)}
                                type="text"
                                value={other}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <Label className="mt-4">
                        {t("settings.signature.form.preview")}
                    </Label>
                    <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHTML(signaturePreview),
                        }}
                        className="rounded-md border border-border bg-white p-4"
                    />
                </div>
                <div className="mt-4 flex justify-between gap-2">
                    <Button
                        disabled={deleteUserSignaturePending}
                        onClick={handleDeleteSignature}
                        type="button"
                        variant="outline"
                    >
                        {deleteUserSignaturePending ? (
                            <Spinner size="small" />
                        ) : (
                            t("settings.signature.form.delete")
                        )}
                    </Button>
                    <Button
                        disabled={
                            createUserSignaturePending ||
                            updateUserSignaturePending
                        }
                        type="submit"
                        variant="purpleGradient"
                    >
                        {createUserSignaturePending ||
                        updateUserSignaturePending ? (
                            <Spinner size="small" />
                        ) : (
                            t("settings.signature.form.save")
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default SignatureSettings;
